// LogDocumentPaymentModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { formatPrice, parsePriceCents } from 'utilities/Currency';
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';

// Local
import { DOCUMENT_PAYMENT_TYPE_KEY } from 'nodetypes/documents/payments/documentPaymentKeys';
import { logDocumentPayment } from 'nodetypes/documents/payments/logDocumentPayment';
import {
  getDocumentPaymentRowModel,
  toDocumentPaymentMoneyNumber,
  readDocumentPaymentField,
} from 'nodetypes/documents/payments/documentPaymentsModel';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

function cleanString(value) {
  return String(value || '').trim();
}

function centsToMajorInput(cents) {
  if (cents == null || !Number.isFinite(Number(cents))) return '';
  return (Number(cents) / 100).toFixed(2);
}

function todayDateInputValue() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Modal: log an offline payment against a DOCUMENT_PAYMENT installment.
 */
const LogDocumentPaymentModal = React.memo(({
  isOpen = false,
  onClose,
  payment = null,
  parentNode = null,
  parentType = null,
  baseKey = '',
  user = null,
  existingCount = 0,
  language = 'en',
  onLogged = null,
}) => {
  const isMobile = useIsMobile();
  const { getType } = useNodeTypes();

  const paymentType = useMemo(() => getType(DOCUMENT_PAYMENT_TYPE_KEY), [getType]);
  const rowModel = useMemo(
    () => (payment ? getDocumentPaymentRowModel(payment, paymentType, language) : null),
    [language, payment, paymentType],
  );

  const scheduledCents = rowModel?.amount
    ?? toDocumentPaymentMoneyNumber(readDocumentPaymentField(payment, 'amount'))
    ?? 0;

  const [amountInput, setAmountInput] = useState('');
  const [method, setMethod] = useState('');
  const [paidDate, setPaidDate] = useState(todayDateInputValue());
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const methodField = useMemo(() => {
    const fields = Array.isArray(paymentType?.schema?.fields) ? paymentType.schema.fields : [];
    return fields.find((field) => cleanString(field?.name) === 'method') || null;
  }, [paymentType]);

  const methodOptions = useMemo(() => {
    const options = methodField ? resolveEnumOptionsForField(methodField, language) : [];
    return options
      .filter((option) => {
        const value = cleanString(option?.value).toLowerCase();
        // Online / credit / retainer are system paths — hide from manual log for Phase A.
        return value && !['online', 'credit', 'retainer'].includes(value);
      })
      .map((option) => ({
        value: cleanString(option.value),
        label: getOptionLabel(option, language) || cleanString(option.value),
      }));
  }, [language, methodField]);

  useEffect(() => {
    if (!isOpen) return;
    setAmountInput(centsToMajorInput(scheduledCents));
    setMethod('');
    setPaidDate(todayDateInputValue());
    setReference('');
    setBusy(false);
    setError('');
  }, [isOpen, payment?.key, scheduledCents]);

  const handleSubmit = useCallback(async () => {
    if (busy || !payment?.key || !parentNode?.key || !user?.key) return;

    const amountCents = parsePriceCents(amountInput);
    if (!amountCents || amountCents <= 0) {
      setError('Enter a payment amount greater than zero.');
      return;
    }
    if (!cleanString(method)) {
      setError('Choose a payment method.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const paidDateIso = paidDate
        ? new Date(`${paidDate}T12:00:00`).toISOString()
        : new Date().toISOString();

      const result = await logDocumentPayment({
        payment,
        parentNode,
        user,
        baseKey: baseKey || null,
        paymentType,
        parentType,
        amountCents,
        method,
        paidDate: paidDateIso,
        reference,
        existingCount,
      });

      toast.success(
        result.remainder
          ? 'Payment logged. Remainder scheduled.'
          : 'Payment logged.',
        { autoClose: 3000 },
      );
      if (typeof onLogged === 'function') onLogged(result);
      onClose?.();
    } catch (err) {
      const message = err?.message || 'Could not log payment.';
      setError(message);
      toast.error(message, { autoClose: 4000 });
    } finally {
      setBusy(false);
    }
  }, [
    amountInput,
    baseKey,
    busy,
    existingCount,
    method,
    onClose,
    onLogged,
    paidDate,
    parentNode,
    parentType,
    payment,
    paymentType,
    reference,
    user,
  ]);

  if (!isOpen || !payment) return null;

  const title = rowModel?.title || 'Payment';
  const hintAmount = scheduledCents > 0 ? formatPrice(scheduledCents) : null;

  return (
    <Modal
      title="Log Payment"
      iconKey="payments"
      isOpen={isOpen}
      onClose={busy ? undefined : onClose}
      width={isMobile ? '100%' : '480px'}
      maxHeight={isMobile ? '100%' : '90vh'}
    >
      <ModalForm>
        <ModalHint>
          {`Logging payment for “${title}”`}
          {hintAmount ? ` (scheduled ${hintAmount})` : ''}
          . Partial amounts create a remainder installment.
        </ModalHint>

        <ModalField label="Amount">
          <TextInput
            value={amountInput}
            onChange={(event) => setAmountInput(event?.target?.value ?? event ?? '')}
            placeholder="0.00"
            disabled={busy}
          />
        </ModalField>

        <ModalField label="Method">
          <SelectInput
            value={method}
            onValueChange={setMethod}
            options={methodOptions}
            placeholder="Select method…"
            disabled={busy}
          />
        </ModalField>

        <ModalField label="Payment date">
          <TextInput
            type="date"
            value={paidDate}
            onChange={(event) => setPaidDate(event?.target?.value ?? event ?? '')}
            disabled={busy}
          />
        </ModalField>

        <ModalField label="Reference">
          <TextInput
            value={reference}
            onChange={(event) => setReference(event?.target?.value ?? event ?? '')}
            placeholder="Check #, confirmation…"
            disabled={busy}
          />
        </ModalField>

        {error ? (
          <ModalHint>
            {error}
          </ModalHint>
        ) : null}
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="END">
          <Button
            label="Cancel"
            onClick={onClose}
            enabled={!busy}
          />
          <Button
            label={busy ? 'Saving…' : 'Log Payment'}
            iconKey="check"
            onClick={handleSubmit}
            enabled={!busy}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

LogDocumentPaymentModal.displayName = 'LogDocumentPaymentModal';

export default LogDocumentPaymentModal;
